import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Loader2, X, Shuffle, Check, Star } from 'lucide-react';
import { toast } from 'sonner';

export const MealSwapDialog = ({
  isOpen, onClose, api,
  planId, weekIndex, mealIndex, originalMeal,
  onSwapApplied,
  favorites = [],
  onFavoriteAdded,
}) => {
  const [loading, setLoading] = useState(false);
  const [alternatives, setAlternatives] = useState([]);
  const [applying, setApplying] = useState(null);
  const [favoriting, setFavoriting] = useState(null);

  const isFavorited = (foods) => {
    if (!foods) return false;
    const sig = foods.trim().toLowerCase().slice(0, 200);
    return favorites.some((f) => f.signature === sig);
  };

  const fetchAlternatives = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().post(`/nutrition-plans/${planId}/swap-meal`, {
        week_index: weekIndex,
        meal_index: mealIndex,
      });
      setAlternatives(res.data.alternatives || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate alternatives');
      onClose();
    }
    setLoading(false);
  }, [api, planId, weekIndex, mealIndex, onClose]);

  useEffect(() => {
    if (isOpen) fetchAlternatives();
    else setAlternatives([]);
  }, [isOpen, fetchAlternatives]);

  const handleApply = async (altIndex) => {
    setApplying(altIndex);
    try {
      const res = await api().post(`/nutrition-plans/${planId}/apply-swap`, {
        week_index: weekIndex,
        meal_index: mealIndex,
        new_meal: alternatives[altIndex],
      });
      toast.success('Meal swapped!');
      if (onSwapApplied) onSwapApplied(res.data.plan);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply swap');
    }
    setApplying(null);
  };

  const handleFavorite = async (altIndex) => {
    setFavoriting(altIndex);
    try {
      const res = await api().post('/nutrition-plans/favorites', { meal: alternatives[altIndex] });
      toast.success(res.data.message || 'Added to favorites!');
      if (onFavoriteAdded) onFavoriteAdded(res.data.favorite);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save favorite');
    }
    setFavoriting(null);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="meal-swap-dialog"
    >
      <div
        className="bg-card border border-border p-6 max-w-2xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading text-xl text-white uppercase tracking-wider flex items-center gap-2">
              <Shuffle className="h-5 w-5 text-green-400" />
              Swap Meal
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              AI alternatives with similar macros — respects your dietary restrictions.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="swap-dialog-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Original meal ref */}
        <div className="bg-black/50 border border-border p-3 rounded mb-4">
          <p className="text-xs text-muted-foreground font-heading uppercase mb-1">
            Currently: {originalMeal.meal} {originalMeal.time && `· ${originalMeal.time}`}
          </p>
          <p className="text-white text-sm mb-1">{originalMeal.foods}</p>
          <div className="flex gap-3 text-xs">
            <span className="text-muted-foreground">{originalMeal.calories} cal</span>
            <span className="text-red-400">{originalMeal.protein_g}g P</span>
            <span className="text-yellow-400">{originalMeal.carbs_g}g C</span>
            <span className="text-blue-400">{originalMeal.fats_g}g F</span>
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-10 w-10 text-green-400 animate-spin" />
            <p className="text-muted-foreground text-sm">Chef AI is cooking up 3 alternatives...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alternatives.map((alt, i) => {
              const fav = isFavorited(alt.foods);
              return (
                <div
                  key={alt.foods || `alt-${i}`}
                  className="bg-black border border-border hover:border-green-500/50 p-4 rounded transition-colors"
                  data-testid={`meal-alternative-${i}`}
                >
                  <p className="text-white text-sm mb-2">{alt.foods}</p>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex gap-3 text-xs">
                      <span className="text-muted-foreground">{alt.calories} cal</span>
                      <span className="text-red-400">{alt.protein_g}g P</span>
                      <span className="text-yellow-400">{alt.carbs_g}g C</span>
                      <span className="text-blue-400">{alt.fats_g}g F</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => !fav && handleFavorite(i)}
                        disabled={fav || favoriting !== null}
                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors border ${
                          fav
                            ? 'text-yellow-400 border-yellow-500/40 cursor-default'
                            : 'text-muted-foreground hover:text-yellow-400 border-border hover:border-yellow-500/40'
                        }`}
                        data-testid={`favorite-alternative-${i}`}
                        title={fav ? 'Already saved' : 'Save to favorites'}
                      >
                        {favoriting === i ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Star className={`h-3 w-3 ${fav ? 'fill-current' : ''}`} />
                        )}
                        {fav ? 'Saved' : 'Save'}
                      </button>
                      <Button
                        size="sm"
                        onClick={() => handleApply(i)}
                        disabled={applying !== null}
                        className="bg-green-600 text-white hover:bg-green-700"
                        data-testid={`apply-alternative-${i}`}
                      >
                        {applying === i ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <><Check className="h-3 w-3 mr-1" /> Use this</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            {alternatives.length === 0 && (
              <p className="text-muted-foreground text-sm text-center py-6">No alternatives available.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
